import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";

import "./dashboard.css";
import "../common/Util.css";
import Loader from "../common/Loader.jsx";
import { useGlobal } from "../../GlobalContext";
import { canList } from "../common/permissionUtils";
import {
  getOpportunityDashboardApi,
  STAGE_COLORS,
} from "../opportunity/opportunity-api";
import filterCalender from "../../assets/svg/filterCalender.svg";

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

const SOURCE_COLORS = ["#6929E2", "#8B53F5", "#A373FF", "#BF9DFF", "#DECDFF", "#EDE4FF"];

const formatMoney = (value) =>
  `₹${(Number(value) || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

// 12.4L / 3.2Cr rather than the full figure: these sit in KPI tiles that are
// too narrow for nine digits.
const formatShort = (value) => {
  const num = Number(value) || 0;
  if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)}Cr`;
  if (num >= 100000) return `₹${(num / 100000).toFixed(2)}L`;
  return formatMoney(num);
};

export const PipelineDashboard = () => {
  const navigate = useNavigate();
  const { rolePermissionList } = useGlobal();
  const hasOpportunityAccess = canList(rolePermissionList, "Opportunity");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!hasOpportunityAccess) {
      setLoading(false);
      return;
    }
    let live = true;
    (async () => {
      try {
        const response = await getOpportunityDashboardApi();
        if (!live) return;
        if (response?.data?.success === true) {
          setData(response.data.data);
        } else {
          setError(response?.data?.message || "Could not load the pipeline");
        }
      } catch (err) {
        if (live) setError("Could not load the pipeline");
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => {
      live = false;
    };
  }, [hasOpportunityAccess]);

  const counts = data?.counts ?? {};
  const stages = data?.stages ?? [];
  const winLoss = data?.win_loss ?? [];
  const topDeals = data?.top_deals ?? [];
  const bySource = data?.by_source ?? [];
  const byOwner = data?.by_owner ?? [];
  const stalled = data?.stalled ?? [];

  const cards = [
    { title: "Pipeline Value", value: formatShort(counts.pipeline_value), note: `${counts.open_count ?? 0} open deals` },
    { title: "Weighted Pipeline", value: formatShort(counts.weighted_pipeline), note: "Discounted by probability" },
    { title: "Win Rate", value: `${counts.win_rate ?? 0}%`, note: "Of closed deals" },
    { title: "Avg Deal Size", value: formatShort(counts.avg_deal_size), note: "Won deals only" },
    { title: "Closing This Month", value: counts.closing_this_month ?? 0, note: "By expected close date" },
  ];

  const winLossChart = {
    labels: winLoss.map((row) => row.month),
    datasets: [
      { label: "Won", data: winLoss.map((row) => row.won), backgroundColor: "#17B65B", borderRadius: 4 },
      { label: "Lost", data: winLoss.map((row) => row.lost), backgroundColor: "#D61916", borderRadius: 4 },
    ],
  };

  const winLossOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: "bottom", labels: { usePointStyle: true, boxWidth: 8 } } },
    scales: {
      x: { grid: { display: false } },
      y: { beginAtZero: true, ticks: { precision: 0 }, grid: { color: "#F1F2F4" } },
    },
  };

  const sourceChart = {
    labels: bySource.map((row) => row.name),
    datasets: [
      {
        data: bySource.map((row) => Number(row.count)),
        backgroundColor: bySource.map((row, i) => SOURCE_COLORS[i % SOURCE_COLORS.length]),
        borderWidth: 0,
        cutout: "70%",
      },
    ],
  };

  const totalBySource = bySource.reduce((sum, row) => sum + Number(row.count), 0);
  const maxOwnerAmount = Math.max(...byOwner.map((row) => Number(row.amount) || 0), 1);

  if (!hasOpportunityAccess) {
    return (
      <div className="w-100 p-3 h-100" style={{ backgroundColor: "#F7F8FC" }}>
        <p className="textLightGrey fs-14 fw-400 text-center py-5 m-0">
          You do not have access to the Opportunity module.
        </p>
      </div>
    );
  }

  return (
    <div className="w-100 p-3 h-100" style={{ backgroundColor: "#F7F8FC" }}>
      <div className="d-flex justify-content-between w-100">
        <div className="header-tabs">
          <button onClick={() => navigate("/dashboard")} className="dashboard-tab-btn">Leads Hub</button>
          <button onClick={() => navigate("/revenue")} className="dashboard-tab-btn">Revenue Overview</button>
          <button onClick={() => navigate("/pipeline")} className="dashboard-tab-btn active">Pipeline</button>
          <button onClick={() => navigate("/forecast")} className="dashboard-tab-btn">Forecast</button>
          <button onClick={() => navigate("/next-action")} className="dashboard-tab-btn">Next Actions</button>
        </div>
        <div className="d-flex gap-3">
          <button
            className="bg-transparent rounded-pill d-flex align-items-center gap-2 px-3 fs-14 fw-600 textPurple"
            style={{ border: "1px solid #6929E2" }}
            onClick={() => navigate("/opportunity/list")}
          >
            <img src={filterCalender} alt="opportunities" />
            View all opportunities
          </button>
        </div>
      </div>

      {loading ? (
        <Loader height="400px" />
      ) : error ? (
        <p className="text-danger fs-14 fw-400 text-center py-5 m-0">{error}</p>
      ) : (
        <>
          {/* KPI cards */}
          <div className="row mt-3">
            {cards.map((card) => (
              <div key={card.title} className="col-xl col-md-4 col-6 mb-3 mb-xl-0">
                <div
                  className="bg-white rounded-4 p-3 d-flex flex-column gap-1 h-100"
                  style={{ minHeight: "110px" }}
                >
                  <p className="textLightGrey fs-13 fw-400 m-0">{card.title}</p>
                  <p className="fs-20 fw-600 textDarkBlue1 m-0">{card.value}</p>
                  <p className="textLightGrey fs-12 fw-400 m-0">{card.note}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Stage strip */}
          <div className="row mt-3">
            <div className="col-12">
              <div className="bg-white rounded-4 p-3">
                <h5 className="fw-semibold mb-3">Pipeline by stage</h5>
                <div className="d-flex gap-2 flex-wrap">
                  {stages.map((stage) => {
                    const colors = STAGE_COLORS[stage.name] ?? { bg: "#F1F2F4", text: "#172B4D" };
                    return (
                      <button
                        key={stage.name}
                        className="border-0 rounded-4 p-3 text-start flex-grow-1"
                        style={{ backgroundColor: colors.bg, color: colors.text, minWidth: "140px" }}
                        onClick={() =>
                          navigate("/opportunity/list", { state: { stage: stage.name } })
                        }
                      >
                        <span className="fs-24 fw-700 d-block">{stage.count}</span>
                        <span className="fs-13 fw-600 d-block">{stage.name}</span>
                        <span className="fs-12 fw-400 d-block">{formatShort(stage.amount)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="row mt-3">
            {/* Win / loss trend */}
            <div className="col-xl-6 mb-3">
              <div className="bg-white rounded-4 p-3 h-100">
                <h5 className="fw-semibold mb-3">Win / loss trend</h5>
                <div style={{ height: "300px" }}>
                  {winLoss.some((row) => row.won || row.lost) ? (
                    <Bar data={winLossChart} options={winLossOptions} />
                  ) : (
                    <p className="textLightGrey fs-13 fw-400 text-center py-5 m-0">
                      No closed deals in the last 12 months.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Source mix */}
            <div className="col-xl-6 mb-3">
              <div className="bg-white rounded-4 p-3 h-100">
                <h5 className="fw-semibold mb-3">Opportunities by source</h5>
                {bySource.length === 0 ? (
                  <p className="textLightGrey fs-13 fw-400 text-center py-5 m-0">
                    No source recorded yet.
                  </p>
                ) : (
                  <div className="row align-items-center">
                    <div className="col-md-6">
                      <div style={{ position: "relative", height: "220px" }}>
                        <Doughnut
                          data={sourceChart}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: { legend: { display: false } },
                          }}
                        />
                        <div
                          style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            textAlign: "center",
                          }}
                        >
                          <div className="fs-24 fw-700 textDarkBlue1">{totalBySource}</div>
                          <div className="fs-13 fw-400 textLightGrey">Total</div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div style={{ maxHeight: "220px", overflowY: "auto" }}>
                        {bySource.map((row, i) => (
                          <div
                            key={row.name}
                            className="d-flex align-items-center justify-content-between gap-2 mb-2"
                          >
                            <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                              <span
                                style={{
                                  width: "10px",
                                  height: "10px",
                                  borderRadius: "50%",
                                  flexShrink: 0,
                                  backgroundColor: SOURCE_COLORS[i % SOURCE_COLORS.length],
                                }}
                              />
                              <span
                                className="fs-13 fw-400 textLightGrey text-truncate"
                                title={row.name}
                              >
                                {row.name}
                              </span>
                            </div>
                            <span className="fs-14 fw-600 textDarkBlue1">{row.count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Top deals */}
            <div className="col-xl-6 mb-3">
              <div className="bg-white rounded-4 p-3 h-100">
                <h5 className="fw-semibold mb-3">Top open deals</h5>
                {topDeals.length === 0 ? (
                  <p className="textLightGrey fs-13 fw-400 text-center py-4 m-0">
                    No open deals.
                  </p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-borderless align-middle mb-0">
                      <thead>
                        <tr className="fs-12 fw-600 textLightGrey">
                          <th>Opportunity</th>
                          <th>Customer</th>
                          <th className="text-end">Value</th>
                          <th>Stage</th>
                          <th className="text-end">Prob.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topDeals.map((deal) => {
                          const colors = STAGE_COLORS[deal.stage] ?? { bg: "#F1F2F4", text: "#172B4D" };
                          return (
                            <tr
                              key={deal.id}
                              style={{ cursor: "pointer", borderTop: "1px solid #F1F2F4" }}
                              onClick={() => navigate("/opportunity/view", { state: { id: deal.id } })}
                            >
                              <td className="fs-13 fw-600 textDarkBlue1">
                                {deal.opportunity_code}
                                <span className="fs-12 fw-400 textLightGrey d-block">
                                  {deal.title}
                                </span>
                              </td>
                              <td className="fs-13 fw-400 textDarkBlue">{deal.party_name || "—"}</td>
                              <td className="fs-13 fw-600 textDarkBlue1 text-end">
                                {deal.currency_symbol
                                  ? `${deal.currency_symbol}${Number(deal.amount).toLocaleString("en-IN")}`
                                  : formatMoney(deal.amount)}
                              </td>
                              <td>
                                <span
                                  className="fs-12 fw-600 px-2 py-1 rounded-pill"
                                  style={{ backgroundColor: colors.bg, color: colors.text }}
                                >
                                  {deal.stage}
                                </span>
                              </td>
                              <td className="fs-13 fw-400 textDarkBlue text-end">
                                {deal.probability ?? 0}%
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {/* Owner leaderboard */}
            <div className="col-xl-6 mb-3">
              <div className="bg-white rounded-4 p-3 h-100">
                <h5 className="fw-semibold mb-3">Pipeline by owner</h5>
                {byOwner.length === 0 ? (
                  <p className="textLightGrey fs-13 fw-400 text-center py-4 m-0">
                    No opportunities assigned to a sales person.
                  </p>
                ) : (
                  byOwner.map((owner) => (
                    <div key={owner.name} className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="fs-13 fw-400 textLightGrey">
                          {owner.name}
                          <span className="fs-12 textLightGrey">
                            {" "}· {owner.count} deals, {owner.won} won
                          </span>
                        </span>
                        <span className="fs-14 fw-600 textDarkBlue1">
                          {formatShort(owner.amount)}
                        </span>
                      </div>
                      <div
                        style={{
                          width: "100%",
                          height: "12px",
                          backgroundColor: "#F1F2F4",
                          borderRadius: "50px",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            width: `${(Number(owner.amount) / maxOwnerAmount) * 100}%`,
                            height: "100%",
                            backgroundColor: "#6929E2",
                            borderRadius: "50px",
                          }}
                        />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Overdue deals */}
            <div className="col-12">
              <div className="bg-white rounded-4 p-3">
                <h5 className="fw-semibold mb-3">Past their expected close date</h5>
                {stalled.length === 0 ? (
                  <p className="textLightGrey fs-13 fw-400 text-center py-4 m-0">
                    Nothing overdue — every open deal is still within its expected close date.
                  </p>
                ) : (
                  stalled.map((deal) => (
                    <div
                      key={deal.id}
                      className="d-flex justify-content-between align-items-center p-3 mb-2 rounded-4"
                      style={{ backgroundColor: "#FDF0F0", cursor: "pointer" }}
                      onClick={() => navigate("/opportunity/view", { state: { id: deal.id } })}
                    >
                      <div>
                        <span className="fs-14 fw-600 textDarkBlue1">
                          {deal.opportunity_code} · {deal.party_name || "—"}
                        </span>
                        <span className="fs-12 fw-400 textLightGrey d-block">
                          {deal.title} — expected {deal.expected_close_date}
                        </span>
                      </div>
                      <div className="d-flex align-items-center gap-3">
                        <span className="fs-14 fw-600 textDarkBlue1">
                          {formatShort(deal.amount)}
                        </span>
                        <span
                          className="fs-12 fw-600 px-2 py-1 rounded-pill"
                          style={{ backgroundColor: "#FFFFFF", color: "#D61916" }}
                        >
                          {deal.days_overdue} days overdue
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PipelineDashboard;
